<template>
  <div>
    <AlertBanner v-if="resendEmailError" class="w-full mt-0 sm:mt-32 lg:mt-0" :status="0"
      >The verification email couldn't be resent. Refresh the page to retry.</AlertBanner
    >
    <div class="w-full max-w-488 mx-auto mt-32 pb-80 md:px-0 lg:mt-48">
      <Card class="w-full p-24">
        <Spinner v-if="loading" :width="96" :height="96" class="mx-auto my-192" />
        <div v-else-if="!loading && reviewTokenValidated && ratingInProgress">
          <SitterDetailsHeader
            :sitterPhotoUrl="reviewee.photoUrl"
            :sitterName="reviewee.displayName"
            :style="{ paddingLeft: 0, margin: 0 }"
          />
          <ExternalParentInfo v-if="step === 'parent_info'" :completeParentInfoForm="handleCompleteParentInfoForm" />
          <StarRatingReview
            v-if="step !== 'parent_info'"
            :ratingSelected="ratingSelected"
            :ratingScore="ratingScore"
            :sitterFirstName="reviewee.firstName"
            class="mt-24"
          />
          <div v-if="step === 'start_review'">
            <PublicFeedback
              v-if="feedbackType === 'public'"
              :returnPath="returnPath"
              :sitterFirstName="reviewee.firstName"
              :score="score"
              :createReview="createReview"
              @update:model-value="updatePublicFeedback"
            />
            <PrivateFeedback
              v-if="feedbackType === 'private'"
              :returnPath="returnPath"
              :sitterFirstName="reviewee.firstName"
              :createPrivateReview="lockPrivateFeedback"
              @update:model-value="updatePrivateFeedback"
            />
          </div>
          <UpdateFeedback
            v-if="step === 'public_feedback'"
            :sitterFirstName="reviewee.firstName"
            :returnPath="returnPath"
            :updateReview="createReview"
            @update:model-value="updatePublicFeedback"
          />
        </div>
        <VerifyYourEmail
          v-else-if="step == 'verify_email' && reviewTokenValidated"
          :segmentPayload="segmentPayload"
          @resend-email-error="handleResendEmailError"
        />
        <GenericError
          v-else-if="step == 'verify_email' && !reviewTokenValidated"
          customCardClasses="box-shadow-none p-8"
          title="Review link expired"
          buttonValue="Contact customer success"
          buttonHref="https://support.sittercity.com/hc/en-us/requests/new"
        >
          This review link has been used before. Ask the sitter to send a new link or contact Customer Success with any questions.
        </GenericError>
        <RatingApiError v-if="ratingError === 'internal_server'" />
      </Card>
    </div>
  </div>
</template>

<script lang="ts">
import AlertBanner from '@/components/alert_banner/AlertBanner.vue';
import Card from '@/components/card/Card.vue';
import Spinner from '@/components/spinner/Spinner.vue';
import SitterDetailsHeader from '../components/SitterDetailsHeader.vue';
import ExternalParentInfo from './ExternalParentInfo.vue';
import StarRatingReview from '../components/StarRatingReview.vue';
import PublicFeedback from '../components/PublicFeedback.vue';
import PrivateFeedback from '../components/PrivateFeedback.vue';
import UpdateFeedback from '../components/UpdateFeedback.vue';
import GenericError from '@/components/generic_error/GenericError.vue';
import { VALIDATE_REVIEW_TOKEN } from '@/graphql/ratings/queries';
import { CREATE_EXTERNAL_REVIEW_MUTATION } from '@/graphql/ratings/mutations';
import RatingApiError from '../RatingApiError.vue';
import VerifyYourEmail from './VerifyYourEmail.vue';
import { sendSegmentEvent } from '@/lib/segment';

interface Reviewee {
  photoUrl: '';
  displayName: '';
  firstName: '';
}

const feedback = {
  FIVE_STARS: 'public',
  FOUR_STARS: 'public',
  THREE_STARS: 'public',
  TWO_STARS: 'private',
  ONE_STAR: 'private',
};

const scores = {
  FIVE_STARS: 5,
  FOUR_STARS: 4,
  THREE_STARS: 3,
  TWO_STARS: 2,
  ONE_STAR: 1,
};

export default {
  name: 'ExternalReviewForm',
  components: {
    AlertBanner,
    Card,
    Spinner,
    SitterDetailsHeader,
    ExternalParentInfo,
    GenericError,
    StarRatingReview,
    PublicFeedback,
    PrivateFeedback,
    RatingApiError,
    UpdateFeedback,
    VerifyYourEmail,
  },
  data() {
    return {
      loading: true,
      step: 'parent_info',
      reviewee: {} as Reviewee,
      reviewTokenValidated: false,
      ratingScore: '',
      publicFeedback: '',
      privateFeedback: '',
      reviewerEmail: '',
      reviewerFirstName: '',
      reviewerLastName: '',
      ratingError: '',
      ratingInProgress: true,
      resendEmailError: false,
    };
  },
  computed: {
    segmentPayload(): object {
      const payload = {
        sitter_uuid: this.sitterId,
        review_id: this.reviewToken,
      } as any;

      if (this.reviewerFirstName) {
        payload.external_reviewer_name = `${this.reviewerFirstName} ${this.reviewerLastName}`;
      }

      if (this.reviewerEmail) {
        payload.external_reviewer_email = this.reviewerEmail;
      }

      return payload;
    },
    feedbackType(): string {
      return feedback[this.ratingScore];
    },
    reviewToken(): string {
      return this.$route.params.reviewToken;
    },
    sitterId(): string {
      return this.$route.params.sitterId;
    },
    returnPath(): string {
      return 'https://www.sittercity.com/';
    },
    score(): number {
      return scores[this.ratingScore];
    },
  },
  watch: {
    step() {
      this.scrollToTop();
    },
  },
  methods: {
    scrollToTop() {
      window.scrollTo(0, 0);
    },
    updatePrivateFeedback(feedbackProp): any {
      this.privateFeedback = feedbackProp;
    },
    updatePublicFeedback(feedbackProp): any {
      this.publicFeedback = feedbackProp;
    },
    handleCompleteParentInfoForm(firstName: string, lastName: string, email: string) {
      this.reviewerEmail = email;
      this.reviewerFirstName = firstName;
      this.reviewerLastName = lastName;
      sendSegmentEvent('Parent External Review Account Created', this.segmentPayload);
      sendSegmentEvent('Parent External Review Started', this.segmentPayload);
      this.step = 'start_review';
    },
    ratingSelected(rating: string): any {
      this.ratingScore = rating;
    },
    lockPrivateFeedback(): any {
      this.step = 'public_feedback';
    },
    handleResendEmailError(hasError: boolean): any {
      this.resendEmailError = hasError;
    },
    createReview(): any {
      this.$apollo
        .mutate({
          mutation: CREATE_EXTERNAL_REVIEW_MUTATION,
          variables: {
            reviewToken: this.reviewToken,
            stars: this.ratingScore,
            publicFeedback: this.publicFeedback,
            privateFeedback: this.privateFeedback,
            reviewerEmail: this.reviewerEmail,
            reviewerFirstName: this.reviewerFirstName,
            reviewerLastName: this.reviewerLastName,
          },
        })
        .then(() => {
          this.step = 'verify_email';
          this.ratingInProgress = false;
          sendSegmentEvent('Parent Email Verification Sent', this.segmentPayload);
        })
        .catch(() => {
          this.ratingError = 'internal_server';
        });
    },
  },
  apollo: {
    externalProviderReviewRequests: {
      query: VALIDATE_REVIEW_TOKEN,
      variables() {
        return {
          reviewToken: this.reviewToken,
        };
      },
      result({ data }) {
        if (data && data.externalProviderReviewRequests?.validateReviewToken) {
          this.reviewee = data.externalProviderReviewRequests?.validateReviewToken?.provider;
          this.reviewTokenValidated = true;
          this.loading = false;
        }
      },
      error(err) {
        this.reviewTokenValidated = false;
        this.loading = false;

        if (err.graphQLErrors[0].message.includes('already associated with a review')) {
          this.step = 'verify_email';
        }
      },
    },
  },
};
</script>
